// src/contexts/AuthContext.js
// OPTIMIZED: Parallel queries, faster timeouts, no duplicate code

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { logLogin, logLogout } from '../services/auditService';
import { AuthContext } from './SharedAuthContext';

// Re-export useAuth from shared context
export { useAuth } from './SharedAuthContext';

const PRACTICE_CACHE_KEY = 'flowrcm_practice_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const isMountedRef = useRef(true);
  const fetchInProgress = useRef(false);

  // Load cached practice on mount
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(PRACTICE_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Only use cache if less than 5 minutes old
        if (Date.now() - timestamp < CACHE_DURATION) {
          setPractice(data);
        }
      }
    } catch (e) {
      console.error('Cache load error:', e);
    }
  }, []);

  const cachePractice = useCallback((data) => {
    if (!isMountedRef.current) return;
    setPractice(data);
    if (data && data.name !== 'Connection Error') {
      try {
        sessionStorage.setItem(PRACTICE_CACHE_KEY, JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.error('Cache save error:', e);
      }
    }
  }, []);

  // Optimized user data fetch with timeout and parallel queries
  const fetchUserData = useCallback(async (userId) => {
    if (fetchInProgress.current) return null;
    fetchInProgress.current = true;

    // Reduced timeout - 3 seconds should be enough for simple queries
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Fetch timeout after 3 seconds')), 3000);
    });

    const fetchPromise = (async () => {
      console.log('[Auth] Fetching user data for:', userId);

      // First get user data (we need practice_id for the next query)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name, approved, active, practice_id, role')
        .eq('id', userId)
        .maybeSingle();

      if (userError) throw userError;
      if (!userData) throw new Error('User record not found');

      // Default practice data
      let practiceData = { name: 'No Practice', id: userData.practice_id };

      // Fetch practice in parallel with returning user data
      if (userData.practice_id) {
        const { data: practiceRecord, error: practiceError } = await supabase
          .from('practices')
          .select('id, name, phone, email, address, pms_system, pms_config')
          .eq('id', userData.practice_id)
          .maybeSingle();

        if (!practiceError && practiceRecord) {
          practiceData = practiceRecord;
        }
      }

      return {
        ...practiceData,
        approved: userData.approved,
        active: userData.active,
        role: userData.role,
        email: userData.email,
        full_name: userData.full_name
      };
    })();

    try {
      return await Promise.race([fetchPromise, timeoutPromise]);
    } catch (error) {
      console.error('[Auth] Fetch error:', error.message);
      throw error;
    } finally {
      fetchInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          setUser(session.user);

          try {
            const data = await fetchUserData(session.user.id);
            if (data && isMountedRef.current) {
              cachePractice(data);
              setAuthError(null);
            }
          } catch (error) {
            console.error('[Auth Init] Error:', error.message);
            setAuthError(error.message);

            // Use cached data or set error state
            const cached = sessionStorage.getItem(PRACTICE_CACHE_KEY);
            if (cached) {
              const { data } = JSON.parse(cached);
              if (data.name !== 'Connection Error') {
                cachePractice(data);
                return;
              }
            }

            // SECURITY: On error, default to NOT approved - never grant access on failure
            cachePractice({
              approved: false,
              active: false,
              role: 'user',
              name: 'Connection Error',
              id: null,
              error: error.message
            });
          }
        } else {
          setUser(null);
          cachePractice(null);
          sessionStorage.removeItem(PRACTICE_CACHE_KEY);
        }
      } catch (error) {
        console.error('[Auth Init] Fatal:', error);
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMountedRef.current) return;

        if (session?.user) {
          setUser(session.user);

          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            try {
              const data = await fetchUserData(session.user.id);
              if (data && isMountedRef.current) {
                cachePractice(data);
                setAuthError(null);
              }
            } catch (error) {
              console.error('[Auth Change] Error:', error.message);
              setAuthError(error.message);
            }
          }
        } else {
          setUser(null);
          cachePractice(null);
          sessionStorage.removeItem(PRACTICE_CACHE_KEY);
        }

        if (isMountedRef.current) setLoading(false);
      }
    );

    return () => {
      isMountedRef.current = false;
      subscription?.unsubscribe();
    };
  }, [fetchUserData, cachePractice]);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    logLogin().catch(err => console.error('Audit log failed:', err));
    return data;
  };

  const signUp = async (email, password, practiceData) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({ email, password });
      if (authError) throw authError;
      if (!authData.user) throw new Error('Signup failed - no user returned');

      const { data: result, error: dbError } = await supabase.rpc('create_user_and_practice', {
        user_id: authData.user.id,
        user_email: email,
        user_full_name: practiceData.fullName,
        practice_name: practiceData.name,
        practice_email: practiceData.email,
        practice_phone: practiceData.phone || ''
      });

      if (dbError) {
        console.error('Database function error:', dbError);
        throw new Error('Failed to create account. Please try again.');
      }

      if (!result || !result.success) {
        throw new Error(result?.error || 'Failed to create account');
      }

      return authData;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    logLogout().catch(err => console.error('Audit log failed:', err));
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    cachePractice(null);
    sessionStorage.removeItem(PRACTICE_CACHE_KEY);
  };

  // Refresh practice data manually
  const refreshPractice = async () => {
    if (user?.id) {
      try {
        const data = await fetchUserData(user.id);
        if (data) cachePractice(data);
      } catch (e) {
        console.error('Refresh failed:', e);
      }
    }
  };

  const value = {
    user,
    practice,
    loading,
    authError,
    signIn,
    signUp,
    signOut,
    refreshPractice
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
